import { z } from 'zod';
import { CHILDREN_SEATING, SEAT_KINDS } from '../utils/mechitza.js';
import { MAX_RENDER_IMAGES, MAX_RENDER_PROMPT_LENGTH, MAX_RENDER_REFERENCES, } from '../types/ai-render.js';
import { ROOM_FEATURE_KINDS, ROOM_OPENING_KINDS } from '../types/room-shape.js';
import { isFloorPlanGridSize, FLOOR_PLAN_GRID_SIZES, PLAN_START_TIME_PATTERN, MAX_ROOM_CAPACITY, } from '../types/floor-plan.js';
const TABLE_TYPES = ['ROUND', 'OVAL', 'SQUARE', 'RECTANGULAR', 'HEAD'];
const ROOM_WALLS = ['north', 'south', 'east', 'west'];
const gridSizeFtSchema = z.number().refine(isFloorPlanGridSize, {
    message: `Grid size must be one of ${FLOOR_PLAN_GRID_SIZES.join(', ')} feet`,
});
/** The part of the evening a plan is for; blank is none. */
const eveningPartSchema = z
    .string()
    .trim()
    .max(100)
    .transform((v) => v || null)
    .nullable();
const startTimeSchema = z
    .string()
    .regex(PLAN_START_TIME_PATTERN, { message: 'Start time must be HH:MM on the 24-hour clock' })
    .nullable();
export const createFloorPlanSchema = z.object({
    eventId: z.string().uuid().optional(),
    roomId: z.string().uuid().optional(),
    name: z.string().min(1).max(255),
    width: z.number().int().min(100).max(10000).optional(),
    height: z.number().int().min(100).max(10000).optional(),
    gridSizeFt: gridSizeFtSchema.optional(),
    eveningPart: eveningPartSchema.optional(),
    startTime: startTimeSchema.optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(255).optional(),
});
export const updateFloorPlanSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    eventId: z.string().uuid().nullable().optional(),
    roomId: z.string().uuid().nullable().optional(),
    width: z.number().int().min(100).max(10000).optional(),
    height: z.number().int().min(100).max(10000).optional(),
    gridSizeFt: gridSizeFtSchema.optional(),
    eveningPart: eveningPartSchema.optional(),
    startTime: startTimeSchema.optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(255).nullable().optional(),
});
export const assignGuestsToTableSchema = z.object({
    guestIds: z.array(z.string().uuid()).min(1).max(50),
    tableNumber: z.number().int().min(1),
    /** The plan to seat on; the event's primary seating plan when left out. */
    floorPlanId: z.string().uuid().optional(),
    /** Seat them even past the table's seat count — a chair squeezed in on purpose. */
    allowOverCapacity: z.boolean().optional(),
});
export const assignGuestToSeatSchema = z.object({
    guestId: z.string().uuid(),
    tableNumber: z.number().int().min(1),
    seatNumber: z.number().int().min(1),
    floorPlanId: z.string().uuid().optional(),
    allowOverCapacity: z.boolean().optional(),
});
// ─── Floor Plan v2 — Room & Object schemas ──────────────────────────────────
const perimeterPointSchema = z.object({
    x: z.number(),
    y: z.number(),
});
const roomShapeVertexSchema = z.object({
    x: z.number().finite().min(-10000).max(10000),
    y: z.number().finite().min(-10000).max(10000),
    bulge: z.number().min(-1).max(1).optional(),
});
const roomFeatureSchema = z.object({
    id: z.string().min(1).max(64),
    kind: z.enum(ROOM_FEATURE_KINDS),
    shape: z.enum(['RECT', 'CIRCLE']),
    x: z.number().finite(),
    y: z.number().finite(),
    widthFt: z.number().min(0.1).max(1000),
    heightFt: z.number().min(0.1).max(1000),
    rotation: z.number().min(0).max(360).optional(),
    label: z.string().max(100).optional(),
    heightFt3d: z.number().min(0).max(200).optional(),
});
const roomOpeningSchema = z.object({
    id: z.string().min(1).max(64),
    kind: z.enum(ROOM_OPENING_KINDS),
    segment: z.number().int().min(0),
    position: z.number().min(0).max(1),
    widthFt: z.number().min(0.5).max(200),
    label: z.string().max(100).optional(),
    swing: z.enum(['IN', 'OUT']).optional(),
    hinge: z.enum(['START', 'END']).optional(),
});
/** A room's drawn shape. Whether it makes a room is checked on the server. */
export const roomShapeSchema = z.object({
    version: z.literal(1),
    outline: z.array(roomShapeVertexSchema).min(2).max(500),
    features: z.array(roomFeatureSchema).max(500),
    openings: z.array(roomOpeningSchema).max(200),
    wallHeightFt: z.number().min(4).max(200).optional(),
    background: z
        .object({
        attachmentId: z.string().uuid(),
        x: z.number().finite(),
        y: z.number().finite(),
        feetPerPixel: z.number().positive().max(100),
        opacity: z.number().min(0).max(1).optional(),
    })
        .nullable()
        .optional(),
});
export const createRoomSchema = z.object({
    venueId: z.string().uuid(),
    name: z.string().min(1).max(255),
    roomType: z.string().max(100).optional(),
    widthFt: z.number().min(1).max(10000),
    heightFt: z.number().min(1).max(10000),
    maxCapacity: z.number().int().min(1).max(MAX_ROOM_CAPACITY).optional(),
    cocktailCapacity: z.number().int().min(1).max(MAX_ROOM_CAPACITY).optional(),
    perimeterPoints: z.array(perimeterPointSchema).min(3).optional(),
    shape: roomShapeSchema.optional(),
});
export const updateRoomSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    roomType: z.string().max(100).nullable().optional(),
    widthFt: z.number().min(1).max(10000).optional(),
    heightFt: z.number().min(1).max(10000).optional(),
    maxCapacity: z.number().int().min(1).max(MAX_ROOM_CAPACITY).nullable().optional(),
    cocktailCapacity: z.number().int().min(1).max(MAX_ROOM_CAPACITY).nullable().optional(),
    perimeterPoints: z.array(perimeterPointSchema).min(3).nullable().optional(),
    /** Setting a shape rewrites the perimeter and the size from it; null clears it. */
    shape: roomShapeSchema.nullable().optional(),
});
export const duplicateFloorPlanSchema = z.object({
    targetEventId: z.string().uuid(),
    name: z.string().min(1).max(255).optional(),
});
// ─── Floor Plan Object Type Config Schemas ──────────────────────────────────
const OBJECT_SHAPES = [
    'RECTANGLE',
    'CIRCLE',
    'OVAL',
    'ROUNDED_RECT',
    'DIAMOND',
    'HEXAGON',
    'SERPENTINE',
    'HALF_ROUND',
];
export const createFloorPlanObjectTypeSchema = z.object({
    name: z.string().min(1).max(255),
    category: z.string().min(1).max(100),
    widthFt: z.number().min(0.1).max(1000),
    heightFt: z.number().min(0.1).max(1000),
    defaultColor: z.string().max(20).optional(),
    defaultSeats: z.number().int().min(0).max(500).optional(),
    icon: z.string().max(100).optional(),
    shape: z.enum(OBJECT_SHAPES).optional(),
    strokeColor: z.string().max(20).optional(),
    canHaveChairs: z.boolean().optional(),
    chairSizeFt: z.number().min(0.1).max(10).optional(),
    isTable: z.boolean().optional(),
    isBackground: z.boolean().optional(),
    aspectRatioLocked: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const updateFloorPlanObjectTypeSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    category: z.string().min(1).max(100).optional(),
    widthFt: z.number().min(0.1).max(1000).optional(),
    heightFt: z.number().min(0.1).max(1000).optional(),
    defaultColor: z.string().max(20).optional(),
    defaultSeats: z.number().int().min(0).max(500).nullable().optional(),
    icon: z.string().max(100).nullable().optional(),
    shape: z.enum(OBJECT_SHAPES).optional(),
    strokeColor: z.string().max(20).nullable().optional(),
    canHaveChairs: z.boolean().optional(),
    chairSizeFt: z.number().min(0.1).max(10).nullable().optional(),
    isTable: z.boolean().optional(),
    isBackground: z.boolean().optional(),
    aspectRatioLocked: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const createRoomEntrySchema = z.object({
    name: z.string().max(100).optional(),
    wall: z.enum(ROOM_WALLS),
    position: z.number().min(0).max(1),
    widthFt: z.number().min(1).max(100).optional(),
});
export const createFloorPlanObjectSchema = z.object({
    objectType: z.string().min(1).max(50),
    label: z.string().max(255).optional(),
    x: z.number(),
    y: z.number(),
    widthFt: z.number().min(0.1).max(1000),
    heightFt: z.number().min(0.1).max(1000),
    rotation: z.number().min(0).max(360).optional(),
    color: z.string().max(20).optional(),
    opacity: z.number().min(0).max(1).optional(),
    seats: z.number().int().min(0).max(500).optional(),
    /**
     * Set when the object is a seatable table (legacy round/oval/etc. or a
     * custom-table object from the Settings library). Null for background
     * art, bars, stages, etc.
     */
    tableNumber: z.number().int().min(0).optional(),
    config: z.record(z.unknown()).optional(),
    sortOrder: z.number().int().optional(),
});
export const updateFloorPlanObjectSchema = z.object({
    label: z.string().max(255).nullable().optional(),
    x: z.number().optional(),
    y: z.number().optional(),
    widthFt: z.number().min(0.1).max(1000).optional(),
    heightFt: z.number().min(0.1).max(1000).optional(),
    rotation: z.number().min(0).max(360).optional(),
    color: z.string().max(20).optional(),
    opacity: z.number().min(0).max(1).optional(),
    seats: z.number().int().min(0).max(500).nullable().optional(),
    tableNumber: z.number().int().min(0).nullable().optional(),
    config: z.record(z.unknown()).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
const objectUpsertItem = z.object({
    id: z.string().uuid().optional(),
    objectType: z.string().min(1).max(50),
    label: z.string().max(255).optional(),
    x: z.number(),
    y: z.number(),
    widthFt: z.number().min(0.1).max(1000),
    heightFt: z.number().min(0.1).max(1000),
    rotation: z.number().min(0).max(360).optional(),
    color: z.string().max(20).optional(),
    opacity: z.number().min(0).max(1).optional(),
    seats: z.number().int().min(0).max(500).optional(),
    tableNumber: z.number().int().min(0).nullable().optional(),
    config: z.record(z.unknown()).optional(),
    sortOrder: z.number().int().optional(),
});
export const bulkUpsertObjectsSchema = z.object({
    objects: z.array(objectUpsertItem).min(0).max(500),
    expectedVersion: z.number().int().min(0).optional(),
    snapshot: z.enum(['MANUAL', 'AUTO']).optional(),
    snapshotLabel: z.string().trim().max(120).optional(),
});
export const restoreFloorPlanVersionSchema = z.object({
    expectedVersion: z.number().int().min(0).optional(),
});
// ─── Auto-assign seating ────────────────────────────────────────────────
export const SEATING_STRATEGIES = [
    'BY_GROUP',
    'BY_SIDE',
    'SEPARATE_MF',
    'KIDS_WITH_ADULTS',
    'KIDS_SEPARATE',
    'MECHITZA',
];
export const autoAssignSeatsSchema = z.object({
    strategy: z.enum(SEATING_STRATEGIES),
    /**
     * Default value applied when a guest's per-row `keepTogether` is null.
     * When the per-guest field is true/false it always overrides this default.
     */
    respectKeepTogether: z.boolean().default(true),
    /** When true, returns the proposed plan + shortfall without persisting. */
    dryRun: z.boolean().default(false),
    /**
     * `unseated` (default) fills seats around everyone already seated; `all`
     * re-seats everyone except pinned seats.
     */
    mode: z.enum(['unseated', 'all']).default('unseated'),
    /**
     * `MECHITZA` only: where the children sit — with the women (the default),
     * with the men, or on whichever side has room.
     */
    childrenWith: z.enum(CHILDREN_SEATING).default('WOMEN'),
    /**
     * If present and there is a shortfall, the algorithm creates extra tables
     * with the given seats-per-table to absorb it, then re-runs the placement.
     */
    autoAddTables: z
        .object({
        seatsPerTable: z.number().int().min(2).max(50),
        tableType: z.enum(TABLE_TYPES).default('ROUND'),
    })
        .optional(),
});
export const aiSeatingSchema = z.object({
    /** When true, returns the AI-proposed plan without persisting. */
    dryRun: z.boolean().default(false),
    /**
     * The reviewed arrangement, sent back on Apply so the persisted plan matches
     * the preview instead of a freshly (and non-deterministically) generated one.
     * Omitted on Preview. Re-validated server-side before persisting.
     */
    reviewedAssignments: z
        .array(z.object({ guestId: z.string(), tableNumber: z.number().int() }))
        .optional(),
});
// ─── AI renders ──────────────────────────────────────────────────────────────
export const createAiRenderSchema = z.object({
    prompt: z
        .string()
        .trim()
        .min(1, 'Say what the picture should look like.')
        .max(MAX_RENDER_PROMPT_LENGTH),
    layoutS3Key: z.string().min(1).max(500),
    referenceAttachmentIds: z.array(z.string().uuid()).max(MAX_RENDER_REFERENCES).optional(),
    count: z.number().int().min(1).max(MAX_RENDER_IMAGES).optional(),
    width: z.number().int().min(64).max(8192).optional(),
    height: z.number().int().min(64).max(8192).optional(),
    layoutStrength: z.number().min(0.2).max(1).optional(),
});
// ─── Seating by seat (/events/:eventId/seating) ─────────────────────────────
export const seatingStateQuerySchema = z.object({
    floorPlanId: z.string().uuid().optional(),
});
export const seatingMovesSchema = z.object({
    floorPlanId: z.string().uuid().optional(),
    moves: z
        .array(z.object({
        guestId: z.string().uuid(),
        partyMemberId: z.string().uuid().optional(),
        tableUid: z.string().min(1).max(200).nullable(),
        seatNumber: z.number().int().min(1).max(500).optional(),
    }))
        .min(1)
        .max(200),
    /** Seat past a table's seat count — a chair squeezed in on purpose. */
    allowOverCapacity: z.boolean().optional(),
});
export const seatingPlanSchema = z.object({
    floorPlanId: z.string().uuid(),
});
export const seatingLockSchema = z.object({
    floorPlanId: z.string().uuid(),
    locked: z.boolean(),
});
/**
 * A whole arrangement put on a plan at once — an auto-assign or AI proposal
 * as the producer reviewed it, or the seating board undoing one: the parties
 * in `reseat` give up their unpinned seats, then each placement takes its
 * seats (the ones named, or the lowest free at its table).
 */
export const seatingArrangementSchema = z.object({
    floorPlanId: z.string().uuid(),
    reseat: z.array(z.string().uuid()).max(2000),
    placements: z
        .array(z.object({
        guestId: z.string().uuid(),
        tableUid: z.string().min(1).max(200),
        seats: z.array(z.number().int().min(1).max(500)).min(1).max(100).optional(),
        count: z.number().int().min(1).max(100).optional(),
        /**
         * Who sits in each of `seats`, in the same order: a named member's id,
         * or null for one of the party's unnamed seats. An undo sends it so
         * each person goes back to their own chair.
         */
        named: z.array(z.string().uuid().nullable()).max(100).optional(),
        /**
         * Who each of `seats` is for — a man, a woman or a child — when the
         * party is seated by section: a named member takes only a seat of
         * their own kind, so no name lands on the wrong side. An undo sends
         * back what each seat held, null for one nobody recorded.
         */
        kinds: z.array(z.enum(SEAT_KINDS).nullable()).max(100).optional(),
    }))
        .max(2000),
});
export const seatingPinSchema = z.object({
    floorPlanId: z.string().uuid(),
    guestIds: z.array(z.string().uuid()).min(1).max(200),
    locked: z.boolean(),
});
//# sourceMappingURL=floor-plan.js.map